import { useApi, isOk } from "../api";
import type {
  InventoryStockRowDto as StockRow,
  InventoryStockPolicyRequest as StockPolicyRequest,
  InventoryExternalDemandGroupDto as ExternalDemandGroup,
  CraftPlanFailure,
} from "../api";
import type { RoleInfo } from "./resourceActions";

export type { RoleInfo, StockRow, StockPolicyRequest, ExternalDemandGroup };

export interface FilterItem {
  itemId: number;
  metadata: number;
}

export interface ChestSlotData {
  slotIndex: number;
  itemId: number;
  metadata: number;
  count: number;
  snbt?: string | null;
  nbtHash?: string | null;
  isCustom: boolean;
}

export interface ItemStackDto {
  key: string;
  itemId: number;
  metadata: number;
  count: number;
  snbt?: string | null;
  nbtHash?: string | null;
  isCustom: boolean;
}

export interface ChestInfo {
  id: string;
  x: number;
  y: number;
  z: number;
  x2?: number | null;
  y2?: number | null;
  z2?: number | null;
  type: number;
  typeName: string;
  filterMode: number;
  filterModeName: string;
  filterItems: FilterItem[];
  lastScannedAt?: string | null;
  suctionEnabled: boolean;
  suctionCheckedAt?: string | null;
}

export interface ChestQuery {
  search?: string;
  type?: number | null;
  filterMode?: number | null;
  neverScanned?: boolean;
  sort?: string;
  desc?: boolean;
  page?: number;
  pageSize?: number;
}

export interface ChestPage {
  total: number;
  page: number;
  pageSize: number;
  chests: ChestInfo[];
}

export interface ChestSelection {
  chestIds?: string[];
  match?: ChestQuery;
}

export interface BulkChestConfig extends ChestSelection {
  type?: number | null;
  filterMode?: number | null;
  filterItems?: FilterItem[];
}

export interface ChestSlotsResponse {
  id: string;
  x: number; y: number; z: number;
  x2?: number | null; y2?: number | null; z2?: number | null;
  type: number; typeName: string;
  lastScannedAt?: string | null;
  slots: ChestSlotData[];
}

export interface StorageInfo {
  id: string;
  name: string;
  location: string;
  citybuild: number;
  citybuildName: string;
  chestCount: number;
  scannedChestCount: number;
  createdAt: string;
  ownerId: string;
  ownerName: string;
  isOwner: boolean;
  myActions: string[];
  reachThroughChests: boolean;
  ignoreLoreCodes: boolean;
  sellChamberBotId?: string | null;
  sellChamberBotName: string;
}

export interface StorageAccessGrant {
  id: string;
  userId: string;
  username: string;
  roleId: string;
  roleName: string;
  expiresAt: string | null;
}

export interface StorageAccessInfo {
  ownerId: string;
  ownerName: string;
  grants: StorageAccessGrant[];
}

export interface TenantAccessGrant {
  id: string;
  tenantId: string;
  tenantName: string;
  roleId: string;
  roleName: string;
  expiresAt: string | null;
}

export type StorageSnapshot = Record<string, number>;
export interface StorageItemsResponse {
  quantities: StorageSnapshot;
  infiniteItems: string[];
  stacks: ItemStackDto[];
}
export type PricingMap = Record<string, { buy?: number; sell?: number }>;

export interface ResolveResult {
  status: string;
  maxCraftable: number;
  hasRecipe: boolean;
  allStatus: string;
  allMaxCraftable: number;
}

export interface RecipeIngredientDetail {
  id: number;
  metadata: number;
  key: string;
  name: string;
  itemName: string;
  count: number;
  stock: number;
  maxCraftable: number;
  hasRecipe: boolean;
}

export interface RecipeDetail {
  resultId: number;
  resultCount: number;
  resultMetadata: number;
  resultKey: string;
  ingredients: RecipeIngredientDetail[];
}

export interface StorageBotAssignmentInfo {
  id: string;
  botRegistryId: string;
  assignedByUserId: string;
  createdAt: string;
  minecraftUsername: string;
  minecraftUuid: string;
}

export interface CreateCraftRequest {
  sourceStorageId: string;
  resultStorageId: string;
  itemKey: string;
  quantity: number;
  itemDisplayName?: string | null;
  botId?: string | null;
}

export interface CraftPlanIngredient {
  itemKey: string;
  displayName: string;
  quantity: number;
}

export type CraftStepDisposition = 'Final' | 'Parked' | 'Buffered';

export interface CraftPlanStep {
  itemKey: string;
  displayName: string;
  times: number;
  resultCount: number;
  producedQuantity: number;
  disposition: CraftStepDisposition;
  estimatedSeconds: number;
  ingredients: CraftPlanIngredient[];
}

export interface CraftShortfall {
  itemKey: string;
  displayName: string;
  needed: number;
  available: number;
}

export interface CraftPlan {
  steps: CraftPlanStep[];
  shortfalls: CraftShortfall[];
  totalConsumed: CraftPlanIngredient[];
  estimatedSeconds: number;
  eligibleBotIds: string[];
  resolvedBotId?: string | null;
  botError?: string | null;
  planError?: string | null;
}

export interface MovePlan {
  eligibleBotIds: string[];
  resolvedBotId?: string | null;
  resolvedBotBusy: boolean;
  botError?: string | null;
}

export interface CraftRequestInfo {
  id: string;
  itemKey: string;
  itemDisplayName: string;
  requestedTimes: number;
  status: string;
}

export interface CraftOrderLineRequest {
  itemKey: string;
  quantity: number;
  itemDisplayName?: string | null;
}

export interface CreateCraftOrderRequest {
  sourceStorageId: string;
  resultStorageId: string;
  lines: CraftOrderLineRequest[];
}

export interface CraftOrderPlanLine {
  lineIndex: number;
  itemKey: string;
  displayName: string;
  quantity: number;
  steps: CraftPlanStep[];
  shortfalls: CraftShortfall[];
  totalConsumed: CraftPlanIngredient[];
  estimatedSeconds: number;
  planError?: string | null;
  planFailure?: CraftPlanFailure | null;
}

export interface CraftOrderPlan {
  lines: CraftOrderPlanLine[];
  totalConsumed: CraftPlanIngredient[];
  estimatedSeconds: number;
  eligibleBotIds: string[];
  botError?: string | null;
}

export interface PlannedOrder {
  sourceStorageId: string;
  sourceStorageName?: string | null;
  plan: CraftOrderPlan;
  sourceError?: string | null;
}

export interface CraftOrderLineInfo {
  id: string;
  lineIndex: number;
  itemKey: string;
  itemDisplayName: string;
  requestedQuantity: number;
  actualCrafted: number;
  assignedBotRegistryId?: string | null;
  status: string;
  statusValue: number;
  startedAt?: string | null;
  completedAt?: string | null;
  failureReason?: string | null;
}

export interface CraftOrderInfo {
  id: string;
  userId: string;
  username: string;
  sourceStorageId: string;
  sourceStorageName: string;
  resultStorageId: string;
  resultStorageName: string;
  status: string;
  statusValue: number;
  startedAt?: string | null;
  completedAt?: string | null;
  failureReason?: string | null;
  createdAt: string;
  lines: CraftOrderLineInfo[];
}

export interface CraftingTaskInfo {
  id: string;
  username?: string;
  taskType: "Craft" | "Transfer";
  itemKey: string;
  quantity: number;
  sourceStorageId: string;
  sourceStorageName?: string;
  resultStorageId: string;
  resultStorageName?: string;
  status: "Pending" | "Running" | "Completed" | "Failed" | "Cancelled";
  errorMessage?: string;
  errorDetail?: string;
  createdAt: string;
  completedAt?: string;
  progress: number;
}

export interface CustomItemDefinition {
  hash: string;
  itemId: number;
  metadata: number;
  displayName?: string;
  snbt: string;
  enchantmentsJson?: string;
  lore?: string;
  damage?: number;
}

export interface AuditLogItem {
  itemKey: string;
  quantity: number;
  displayName?: string;
}

export interface InventoryAuditLogEntry {
  id: string;
  userId: string;
  username: string;
  actionType: string;
  sourceStorageId?: string;
  sourceStorageName?: string;
  targetStorageId?: string;
  targetStorageName?: string;
  itemsJson: string;
  note?: string;
  relatedTaskId?: string;
  createdAt: string;
}

export interface AuditLogPage {
  items: InventoryAuditLogEntry[];
  total: number;
}

export interface AuditLogQuery {
  actionType?: string;
  from?: string;
  to?: string;
  filterUserId?: string;
  page?: number;
  pageSize?: number;
}

export interface MoveLine {
  itemKey: string;
  quantity: number;
  itemDisplayName?: string;
}

export interface MoveRequest {
  id: string;
  userId: string;
  username: string;
  sourceStorageId: string;
  sourceStorageName: string;
  destinationStorageId: string;
  destinationStorageName: string;
  itemKey: string;
  itemDisplayName: string;
  requestedQuantity: number;
  actualQuantityMoved: number;
  assignedBotRegistryId?: string | null;
  status: string;
  statusValue: number;
  startedAt?: string;
  completedAt?: string;
  failureReason?: string;
  createdAt: string;
}

export interface TenantInventorySettings {
  defaultProfitMargin: number;
}

export interface StorageCreatedSummary {
  id: string;
  name: string;
  createdAt: string;
}

function throwFromEnvelope(env: any): never {
  const errors = env?.errors ?? env?.Errors;
  if (errors) {
    const messages: string[] = [];
    for (const key in errors) {
      const vals = errors[key];
      if (Array.isArray(vals)) messages.push(...vals);
      else if (typeof vals === "string") messages.push(vals);
    }
    throw new Error(messages.join("; ") || "Unknown error");
  }
  throw new Error(env?.message ?? env?.Message ?? "Request failed");
}

function unwrap<T>(res: { data: any }, key?: string): T {
  if (!isOk(res.data)) throwFromEnvelope(res.data);
  const v = res.data.value;
  return (key ? v?.[key] : v) as T;
}

export function useInventoryApi() {
  const api = useApi();

  return {
    async fetchStorages(): Promise<StorageInfo[]> {
      const res = await api.inventory.getStorages();
      return unwrap<StorageInfo[]>(res, "storages") ?? [];
    },

    async createStorage(name: string, location?: string, citybuild?: number): Promise<StorageCreatedSummary> {
      const res = await api.inventory.createStorage({ name, location, citybuild });
      return unwrap<StorageCreatedSummary>(res);
    },

    async updateStorage(id: string, data: {
      name?: string;
      location?: string;
      citybuild?: number;
      chests?: { x: number; y: number; z: number }[];
      reachThroughChests?: boolean;
      ignoreLoreCodes?: boolean;
    }): Promise<void> {
      const res = await api.inventory.updateStorage(id, data as any);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async deleteStorage(id: string): Promise<void> {
      const res = await api.inventory.deleteStorage(id);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async updateChestConfig(
      storageId: string,
      chestId: string,
      data: { type: number; filterMode: number; filterItems?: FilterItem[]; x2?: number | null; y2?: number | null; z2?: number | null }
    ): Promise<ChestInfo> {
      const res = await api.inventory.updateChestConfig(storageId, chestId, data as any);
      return unwrap<ChestInfo>(res);
    },

    async fetchChests(storageId: string, query: ChestQuery = {}): Promise<ChestPage> {
      const res = await api.inventory.getChests(storageId, {
        search: query.search || undefined,
        type: query.type ?? undefined,
        filterMode: query.filterMode ?? undefined,
        neverScanned: query.neverScanned || undefined,
        sort: query.sort || undefined,
        desc: query.desc || undefined,
        page: query.page ?? 0,
        pageSize: query.pageSize ?? 50,
      } as never);
      return unwrap<ChestPage>(res);
    },

    async fetchAllChests(storageId: string): Promise<ChestInfo[]> {
      const pageSize = 200;
      const first = await this.fetchChests(storageId, { page: 0, pageSize });
      const chests = [...first.chests];
      const pages = Math.ceil(first.total / pageSize);
      for (let page = 1; page < pages; page++) {
        chests.push(...(await this.fetchChests(storageId, { page, pageSize })).chests);
      }
      return chests;
    },

    async bulkUpdateChests(storageId: string, body: BulkChestConfig): Promise<number> {
      const res = await api.inventory.bulkUpdateChestConfig(storageId, body as never);
      return unwrap<{ updated: number }>(res).updated;
    },

    async readChests(storageId: string, chestIds?: string[], botId?: string | null): Promise<void> {
      const res = await api.inventory.runReadChests(storageId, { botId: botId ?? null, chestIds } as never);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async getChestSlots(storageId: string, chestId: string): Promise<ChestSlotsResponse> {
      const res = await api.inventory.getChestSlots(storageId, chestId);
      return unwrap<ChestSlotsResponse>(res);
    },

    async addChest(storageId: string, x: number, y: number, z: number): Promise<void> {
      const res = await api.inventory.addChest(storageId, { x, y, z } as never);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    // No selection deletes every chest of the storage, which is what the danger-zone reset wants.
    async deleteChests(storageId: string, selection?: ChestSelection): Promise<number> {
      const res = await api.inventory.deleteAllChests(storageId, (selection ?? {}) as never);
      return unwrap<{ deleted: number }>(res).deleted;
    },

    async fetchStorageItems(id: string): Promise<StorageItemsResponse> {
      const res = await api.inventory.getStorageItems(id);
      return unwrap<StorageItemsResponse>(res);
    },

    async fetchAllStorageItems(): Promise<StorageItemsResponse> {
      const res = await api.inventory.getAllStorageItems();
      return unwrap<StorageItemsResponse>(res);
    },

    async setStorageItems(id: string, snapshot: StorageSnapshot): Promise<void> {
      const res = await api.inventory.setStorageItems(id, snapshot as any);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async resolveItem(storageId: string, itemKey: string): Promise<ResolveResult> {
      const res = await api.inventory.resolveItem(storageId, itemKey);
      return unwrap<ResolveResult>(res);
    },

    async resolveBatch(storageId: string, itemKeys: string[]): Promise<Record<string, ResolveResult>> {
      const res = await api.inventory.resolveBatch({ storageId, itemKeys } as any);
      return unwrap<Record<string, ResolveResult>>(res, "results") ?? {};
    },

    async fetchRecipeDetail(id: number, metadata: number, storageId?: string, hash?: string): Promise<RecipeDetail | null> {
      const params = storageId || hash ? { storageId, hash } : undefined;
      const res = await api.inventory.getRecipeDetail(id, metadata, params);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
      return (res.data.value ?? null) as RecipeDetail | null;
    },

    async fetchReservations(storageId: string): Promise<StorageSnapshot> {
      const res = await api.inventory.getReservations(storageId);
      return unwrap<StorageSnapshot>(res, "reservations") ?? {};
    },

    async fetchStorageBots(storageId: string): Promise<StorageBotAssignmentInfo[]> {
      const res = await api.inventory.getStorageBots(storageId);
      return unwrap<StorageBotAssignmentInfo[]>(res, "bots") ?? [];
    },

    async assignStorageBot(storageId: string, botId: string): Promise<void> {
      const res = await api.inventory.assignStorageBot(storageId, { botId } as any);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async unassignStorageBot(storageId: string, botId: string): Promise<void> {
      const res = await api.inventory.unassignStorageBot(storageId, botId);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async planCraft(req: CreateCraftRequest): Promise<CraftPlan> {
      const res = await api.inventory.planCraft(req as any);
      return unwrap<CraftPlan>(res);
    },

    async createCraftRequest(req: CreateCraftRequest): Promise<CraftRequestInfo> {
      const res = await api.inventory.createCraftRequest(req as any);
      return unwrap<CraftRequestInfo>(res);
    },

    async cancelCraftRequest(id: string): Promise<void> {
      const res = await api.inventory.cancelCraftRequest(id);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async planCraftOrder(req: CreateCraftOrderRequest): Promise<CraftOrderPlan> {
      const res = await api.inventory.planCraftOrder(req as any);
      return unwrap<CraftOrderPlan>(res);
    },

    async createCraftOrder(req: CreateCraftOrderRequest): Promise<CraftOrderInfo> {
      const res = await api.inventory.createCraftOrder(req as any);
      return unwrap<CraftOrderInfo>(res);
    },

    async fetchCraftOrders(): Promise<CraftOrderInfo[]> {
      const res = await api.inventory.getCraftOrders();
      return unwrap<{ orders: CraftOrderInfo[] }>(res).orders;
    },

    async cancelCraftOrder(id: string): Promise<void> {
      const res = await api.inventory.cancelCraftOrder(id);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async cancelCraftOrderLine(orderId: string, lineId: string): Promise<void> {
      const res = await api.inventory.cancelCraftOrderLine(orderId, lineId);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async fetchStock(storageId: string): Promise<StockRow[]> {
      const res = await api.inventory.getStock(storageId);
      return unwrap<{ rows: StockRow[] }>(res).rows;
    },

    async fetchPlannedOrders(storageId: string): Promise<PlannedOrder[]> {
      const res = await api.inventory.getPlannedOrders(storageId);
      return unwrap<{ orders: PlannedOrder[] }>(res).orders;
    },

    async fetchExternalDemand(storageId: string): Promise<ExternalDemandGroup[]> {
      const res = await api.inventory.getExternalDemand(storageId);
      return unwrap<{ groups: ExternalDemandGroup[] }>(res).groups;
    },

    async saveStock(storageId: string, upserts: StockPolicyRequest[], removedItemKeys: string[]): Promise<StockRow[]> {
      const res = await api.inventory.saveStock(storageId, { upserts, removedItemKeys });
      return unwrap<{ rows: StockRow[] }>(res).rows;
    },

    async fetchTasks(): Promise<CraftingTaskInfo[]> {
      const res = await api.inventory.getTasks();
      return unwrap<CraftingTaskInfo[]>(res, "tasks") ?? [];
    },

    async cancelTask(taskId: string): Promise<void> {
      const res = await api.inventory.cancelTask(taskId);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async fetchPricing(): Promise<PricingMap> {
      const res = await api.inventory.getPricing();
      return unwrap<PricingMap>(res, "items") ?? {};
    },

    async savePricing(pricing: PricingMap): Promise<void> {
      const res = await api.inventory.setPricing(pricing as any);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async fetchCustomItems(): Promise<CustomItemDefinition[]> {
      const res = await api.inventory.getCustomItems();
      return unwrap<CustomItemDefinition[]>(res, "items") ?? [];
    },

    async fetchAuditLog(storageId: string, query?: AuditLogQuery): Promise<AuditLogPage> {
      const res = await api.inventoryAudit.getAuditLog(storageId, query as any);
      return unwrap<AuditLogPage>(res);
    },

    async createManualAuditEntry(
      storageId: string,
      actionType: "input" | "extraction",
      items: Record<string, number>,
      note?: string
    ): Promise<void> {
      const res = await api.inventoryAudit.createManualAuditEntry(storageId, { actionType, items, note } as any);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async createMoveRequest(
      sourceStorageId: string,
      destinationStorageId: string,
      lines: MoveLine[],
      botId?: string | null,
    ): Promise<MoveRequest[]> {
      const res = await api.inventory.createMoveRequest({
        sourceStorageId,
        destinationStorageId,
        lines,
        botId: botId ?? null,
      } as any);
      return unwrap<MoveRequest[]>(res, "moves") ?? [];
    },

    async planMove(sourceStorageId: string, destinationStorageId: string, botId?: string | null): Promise<MovePlan> {
      const res = await api.inventory.planMove({ sourceStorageId, destinationStorageId, botId: botId ?? null } as any);
      return unwrap<MovePlan>(res);
    },

    async fetchMoveRequests(storageId?: string): Promise<MoveRequest[]> {
      const res = await api.inventory.getMoveRequests(storageId ? { storageId } : undefined);
      return unwrap<MoveRequest[]>(res, "moves") ?? [];
    },

    async cancelMoveRequest(id: string): Promise<void> {
      const res = await api.inventory.cancelMoveRequest(id);
      if (!isOk(res.data)) throwFromEnvelope(res.data);
    },

    async fetchTenantSettings(): Promise<TenantInventorySettings> {
      const res = await api.inventory.getTenantSettings();
      return unwrap<TenantInventorySettings>(res);
    },
  };
}
